import { isApiError } from "@sagot/api-client";
import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, EmptyState, ErrorState, Icon, Progress, Segmented, TableSkeletonRows } from "@sagot/ui";
import { useState } from "react";
import { formatDate, formatNumber } from "../shared/format";
import type { ChannelOut } from "../shared/types";
import { CHANNEL_ICON, formatResponse, percent, performanceChannels } from "./channels-model";
import { useChannelSummaries, type AnalyticsRange, type SummaryOut } from "./use-channels";

const P = "deployKnowledge.channels.performance";
const COLUMNS = 5;
const FORBIDDEN = 403;

interface RowProps {
  channel: ChannelOut;
  summary: SummaryOut | undefined;
  wsSlug: string;
}

function PerformanceRow({ channel, summary, wsSlug }: RowProps) {
  const { t, i18n } = useTranslation();
  const name = t(`deployKnowledge.channels.names.${channel.type}`);
  const rate = percent(summary?.ai_resolution_rate);
  return (
    <tr>
      <td>
        <AppLink href={consoleRoutes.analytics(wsSlug)} className="flex items-center gap-2.5 font-semibold hover:text-primary-ink">
          <Icon name={CHANNEL_ICON[channel.type]} className="size-4 text-fg-subtle" />
          {name}
        </AppLink>
      </td>
      <td className="text-right tabular-nums">{summary ? formatNumber(summary.conversations, i18n.language) : "—"}</td>
      <td>
        {rate === null ? (
          <span className="text-fg-subtle">—</span>
        ) : (
          <div className="flex items-center gap-2">
            <Progress className="w-24" size="sm" value={rate} label={t(`${P}.resolutionLabel`, { channel: name })} />
            <span className="tabular-nums text-xs font-semibold">{rate}%</span>
          </div>
        )}
      </td>
      <td className="text-right font-mono text-xs">{formatResponse(summary?.avg_response_ms)}</td>
      <td className={summary?.csat_avg == null ? "text-right tabular-nums text-fg-subtle" : "text-right tabular-nums"}>
        {summary?.csat_avg == null ? "—" : summary.csat_avg.toFixed(1)}
      </td>
    </tr>
  );
}

interface PerformanceProps {
  channels: readonly ChannelOut[];
  agentId: string;
  agentName: string;
  wsSlug: string;
}

/** "Channel performance" card: one `GET /v1/analytics/summary?channel=` per active channel. */
export function ChannelPerformance({ channels, agentId, agentName, wsSlug }: PerformanceProps) {
  const { t, i18n } = useTranslation();
  const [range, setRange] = useState<AnalyticsRange>("7d");
  const rows = performanceChannels(channels);
  const summaries = useChannelSummaries(agentId, range, rows.map((c) => c.type));
  const period = summaries.find((q) => q.data)?.data?.period;
  const failed = summaries.find((q) => q.isError);
  const pending = summaries.some((q) => q.isPending);
  const body = () => {
    if (failed) {
      const forbidden = isApiError(failed.error) && failed.error.status === FORBIDDEN;
      return (
        <tr>
          <td colSpan={COLUMNS}>
            {forbidden ? (
              <EmptyState icon="lock" headingLevel={3} title={t(`${P}.noAccess`)} />
            ) : (
              <ErrorState title={t(`${P}.loadError`)} onRetry={() => { summaries.forEach((q) => void q.refetch()); }} retryLabel={t("common.retry")} />
            )}
          </td>
        </tr>
      );
    }
    if (pending) return <TableSkeletonRows rows={Math.max(rows.length, 3)} columns={COLUMNS} />;
    if (rows.length === 0) {
      return (
        <tr>
          <td colSpan={COLUMNS}>
            <EmptyState icon="bar-chart" headingLevel={3} title={t(`${P}.emptyTitle`)} description={t(`${P}.emptyBody`)} />
          </td>
        </tr>
      );
    }
    return rows.map((channel, i) => <PerformanceRow key={channel.type} channel={channel} summary={summaries[i]?.data} wsSlug={wsSlug} />);
  };
  return (
    <section className="card overflow-hidden" aria-labelledby="channel-performance-title">
      <div className="card-header">
        <div>
          <h2 id="channel-performance-title" className="card-title">
            {t(`${P}.title`)}
          </h2>
          <div className="mt-0.5 text-xs text-fg-subtle">
            {period ? t(`${P}.period`, { start: formatDate(period.start, i18n.language), end: formatDate(period.end, i18n.language), agent: agentName }) : agentName}
          </div>
        </div>
        <Segmented aria-label={t(`${P}.range`)} value={range} onValueChange={setRange} options={[{ value: "7d", label: t(`${P}.days7`) }, { value: "30d", label: t(`${P}.days30`) }]} />
      </div>
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>{t(`${P}.colChannel`)}</th>
              <th className="text-right">{t(`${P}.colConversations`)}</th>
              <th>{t(`${P}.colResolution`)}</th>
              <th className="text-right">{t(`${P}.colResponse`)}</th>
              <th className="text-right">{t(`${P}.colCsat`)}</th>
            </tr>
          </thead>
          <tbody>{body()}</tbody>
        </table>
      </div>
    </section>
  );
}
